// Considered: PerspectivePicker — rejected because it commits on select; this flow needs a preview step
// between select and commit that shows who gains and loses access. See spec Reuse Audit.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ApiClient } from '@center-technologies/shared-api-clients';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import type { PurposeParentPreview, PurposePathStep, SpherePurpose } from 'types/purpose';
import {
  PurposeApiError,
  previewSpherePurposeParent,
  setSpherePurposeParent,
} from '../../../api/purposeApi';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import SkeletonAtom from '../../atoms/Skeleton/SkeletonAtom';
import DialogWrapper from '../DialogWrapper/DialogWrapper';
import PickerShellMolecule, { PickerShellBucket } from '../PickerShell/PickerShellMolecule';
import SphereSearch, { SphereOption } from '../SphereSearch/SphereSearch';
import './PurposeParentDialogMolecule.scss';

export interface PurposeParentCandidate {
  /** `null` is the "sit directly in the Center" choice. */
  id: string | null;
  name: string;
  glyph?: string;
}

type CandidateGroup = 'root' | 'path' | 'siblings' | 'children';
interface PickerItem extends PurposeParentCandidate {
  group: CandidateGroup;
  /** The "Don't know yet" choice: no parent, listed under "Needs a home". */
  unplaced?: boolean;
}

export interface PurposeParentDialogMoleculeProps {
  visible: boolean;
  onHide: () => void;
  graphClient: ApiClient | null;
  sphereId: string;
  sphereName?: string;
  /** Enables the org-wide search under the suggestions. */
  organizationId?: string;
  /** The sphere's current Purpose path; visible purpose steps are listed first. */
  path?: PurposePathStep[];
  siblings?: PurposeParentCandidate[];
  /** The sphere's own Purpose-tree children — not React's `children`, hence the longer name. */
  nearbyChildren?: PurposeParentCandidate[];
  /** True while `siblings` / `nearbyChildren` are being read, so the picker shows a skeleton rather than a bare search box. */
  nearbyLoading?: boolean;
  /** Revision the purpose was read at; a stale one is answered with a 409 and the dialog stays open. */
  revision?: string;
  /** Called with the refreshed purpose after a confirmed change. */
  onMoved: (purpose: SpherePurpose) => void;
  /** Called on a stale-revision 409 so the host can reload the latest value. */
  onStale?: () => void;
}

type PreviewState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; preview: PurposeParentPreview }
  | { status: 'error' };

const SAMPLE_LIMIT = 3;

const ERROR_KEYS: Partial<Record<string, string>> = {
  purpose_cycle: 'purpose.dialog.errors.cycle',
  manage_security_required: 'purpose.dialog.errors.requiresManageSecurity',
  permission_service_unreachable: 'purpose.dialog.errors.unavailable',
  parent_not_found_or_not_discoverable: 'purpose.dialog.errors.notDiscoverable',
  stale_revision: 'purpose.dialog.errors.stale',
};

const PurposeParentDialogMolecule: React.FC<PurposeParentDialogMoleculeProps> = ({
  visible,
  onHide,
  graphClient,
  sphereId,
  sphereName = '',
  organizationId,
  path = [],
  siblings = [],
  nearbyChildren = [],
  nearbyLoading = false,
  revision,
  onMoved,
  onStale,
}) => {
  const { t, i18n } = useTranslation();
  const logAnalyticsEvent = useAnalytics();
  const [selected, setSelected] = useState<PickerItem | null>(null);
  const [preview, setPreview] = useState<PreviewState>({ status: 'idle' });
  const [saving, setSaving] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const requestId = useRef(0);

  useEffect(() => {
    if (!visible) {
      requestId.current += 1;
      setSelected(null);
      setPreview({ status: 'idle' });
      setSaving(false);
      setErrorKey(null);
    }
  }, [visible]);

  const items = useMemo<PickerItem[]>(() => {
    const seen = new Set<string>([sphereId]);
    const out: PickerItem[] = [
      { id: null, name: t('purpose.dialog.removeParent'), glyph: 'lucide:building', group: 'root' },
      { id: null, name: t('purpose.field.dontKnowYet'), glyph: 'lucide:circle-help', group: 'root', unplaced: true },
    ];
    const add = (group: CandidateGroup, candidates: PurposeParentCandidate[]) => {
      candidates.forEach((c) => {
        if (c.id === null || seen.has(c.id)) return;
        seen.add(c.id);
        out.push({ ...c, group });
      });
    };
    add(
      'path',
      path.filter((s) => s.via === 'purpose' && s.access === 'visible').map((s) => ({ id: s.id, name: s.name, glyph: s.glyph }))
    );
    add('siblings', siblings);
    add('children', nearbyChildren);
    return out;
  }, [path, siblings, nearbyChildren, sphereId, t]);

  const computeBuckets = useCallback(
    (filtered: PickerItem[]): PickerShellBucket<PickerItem>[] => {
      const labels: Record<CandidateGroup, string> = {
        root: '',
        path: t('purpose.dialog.currentPath'),
        siblings: t('purpose.dialog.siblings'),
        children: t('purpose.dialog.children'),
      };
      const order: CandidateGroup[] = ['root', 'path', 'siblings', 'children'];
      return order
        .map((group) => ({ key: group, label: labels[group], items: filtered.filter((i) => i.group === group) }))
        .filter((bucket) => bucket.items.length > 0);
    },
    [t]
  );

  const runPreview = useCallback(
    async (candidate: PickerItem) => {
      if (!graphClient) return;
      const id = ++requestId.current;
      setSelected(candidate);
      setErrorKey(null);
      setPreview({ status: 'loading' });
      try {
        const result = await previewSpherePurposeParent(graphClient, sphereId, candidate.id);
        if (id === requestId.current) setPreview({ status: 'ready', preview: result });
      } catch (err) {
        if (id !== requestId.current) return;
        const key = err instanceof PurposeApiError ? ERROR_KEYS[err.code] : undefined;
        if (key) {
          setErrorKey(key);
          setPreview({ status: 'idle' });
          setSelected(null);
        } else {
          setPreview({ status: 'error' });
        }
      }
    },
    [graphClient, sphereId]
  );

  const handleSearchSelected = useCallback(
    (_all: SphereOption[], one?: SphereOption | null) => {
      if (!one?.code) return;
      void runPreview({ id: one.code, name: one.name, glyph: one.glyph, group: 'siblings' });
    },
    [runPreview]
  );

  const confirm = useCallback(async () => {
    if (!graphClient || !selected || preview.status !== 'ready') return;
    setSaving(true);
    setErrorKey(null);
    try {
      const next = await setSpherePurposeParent(graphClient, sphereId, {
        parentId: selected.id,
        unplaced: selected.unplaced,
        baseRevision: revision,
      });
      // Emitted here rather than from the axios pattern in analyticsConfig: `accessChanged` is the
      // preview's verdict for the move just confirmed, and the PUT's response does not carry it.
      logAnalyticsEvent('purpose_parent_changed', undefined, {
        sphereLabel: sphereId,
        toUnplaced: selected.unplaced === true,
        toCenterRoot: selected.id === null && selected.unplaced !== true,
        accessChanged: preview.preview.accessChanges,
      });
      onMoved(next);
      onHide();
    } catch (err) {
      const code = err instanceof PurposeApiError ? err.code : undefined;
      if (code === 'stale_revision') onStale?.();
      setErrorKey((code && ERROR_KEYS[code]) || 'purpose.dialog.errors.generic');
    } finally {
      setSaving(false);
    }
  }, [graphClient, selected, preview, sphereId, revision, onMoved, onHide, onStale, logAnalyticsEvent]);

  const cancelSelection = useCallback(() => {
    requestId.current += 1;
    setSelected(null);
    setPreview({ status: 'idle' });
    setErrorKey(null);
  }, []);

  const renderSummary = (p: PurposeParentPreview) => {
    if (!p.accessChanges) return <p className="purpose-parent-dialog__no-change">{p.private ? t('purpose.dialog.privateNoChange') : t('purpose.dialog.noChange')}</p>;
    const diff = (kind: 'gains' | 'loses', heading: string) => {
      const d = p[kind];
      if (d.count === 0) return null;
      const shown = d.sample.slice(0, SAMPLE_LIMIT);
      const more = d.count - shown.length;
      return (
        <div className="purpose-parent-dialog__diff" key={kind}>
          <div className="purpose-parent-dialog__diff-heading">{heading}</div>
          <ul className="purpose-parent-dialog__samples">
            {shown.map((s) => (
              <li key={s.id}>{s.name}</li>
            ))}
            {more > 0 && <li>{t('purpose.dialog.andMore', { count: more })}</li>}
          </ul>
        </div>
      );
    };
    return (
      <>
        {diff('gains', t('purpose.dialog.gainsHeading'))}
        {diff('loses', t('purpose.dialog.losesHeading'))}
        {p.truncated && <p className="purpose-parent-dialog__truncated">{t('purpose.dialog.truncated')}</p>}
      </>
    );
  };

  const summaryLine = (p: PurposeParentPreview): string => {
    if (!p.accessChanges) {
      return p.private ? t('purpose.dialog.privateNoChange') : t('purpose.dialog.noChange');
    }
    const gains = p.truncated
      ? t('purpose.dialog.gainsAtLeast', { count: p.gains.count, total: p.gains.count.toLocaleString(i18n.language) })
      : t('purpose.dialog.gains', { count: p.gains.count });
    const loses = p.truncated
      ? t('purpose.dialog.losesAtLeast', { count: p.loses.count, total: p.loses.count.toLocaleString(i18n.language) })
      : t('purpose.dialog.loses', { count: p.loses.count });
    return t('purpose.dialog.previewSummary', { gains, loses });
  };

  return (
    <DialogWrapper
      header={t('purpose.dialog.title')}
      visible={visible}
      onHide={onHide}
      className="purpose-parent-dialog"
      style={{ width: 'min(40rem, 100vw)' }}
    >
      <div className="purpose-parent-dialog__body" data-testid="purpose-parent-dialog">
        {errorKey && (
          <p className="purpose-parent-dialog__error" role="alert">
            {t(errorKey)}
          </p>
        )}
        {nearbyLoading && (
          <output className="purpose-parent-dialog__nearby-loading" aria-label={t('purpose.dialog.loadingNearby')}>
            <SkeletonAtom count={3} height="2rem" gap="var(--spacing-sm)" />
          </output>
        )}
        <PickerShellMolecule<PickerItem>
          items={items}
          getSearchText={(item) => item.name}
          getRowKey={(item) => item.id ?? (item.unplaced ? 'unplaced' : 'root')}
          computeBuckets={computeBuckets}
          onPickItem={(item) => void runPreview(item)}
          isItemSelected={(item) => selected !== null && item.id === selected.id && Boolean(item.unplaced) === Boolean(selected.unplaced)}
          renderRow={(item) => (
            <span className="purpose-parent-dialog__row">
              {item.glyph && <GlyphAtom glyph={item.glyph} fallbackText={item.name} aria-hidden />}
              <span>{item.name}</span>
            </span>
          )}
          searchPlaceholder={t('purpose.dialog.searchLabel')}
          listLabel={t('purpose.dialog.title')}
          noMatchesMessage={t('purpose.dialog.empty')}
          onCancel={onHide}
          cancelLabel={t('purpose.dialog.cancel')}
          testIdPrefix="purpose-parent"
          listFooter={
            organizationId ? (
              <div className="purpose-parent-dialog__search">
                <SphereSearch
                  organizationId={organizationId}
                  single
                  showLabel
                  label={t('purpose.dialog.searchLabel')}
                  placeholder={t('purpose.dialog.searchLabel')}
                  onSphereSelected={handleSearchSelected}
                />
              </div>
            ) : undefined
          }
        />
        {selected && (
          <section className="purpose-parent-dialog__confirm" aria-live="polite" data-testid="purpose-parent-confirm">
            <h3 className="purpose-parent-dialog__confirm-title">
              {t('purpose.dialog.confirmTitle', { name: selected.name })}
            </h3>
            {preview.status === 'loading' && (
              <output aria-label={t('purpose.dialog.previewing')}>
                <SkeletonAtom />
              </output>
            )}
            {preview.status === 'error' && (
              <div className="purpose-parent-dialog__error" role="alert">
                <span>{t('purpose.dialog.previewError')}</span>
                <ButtonAtom
                  type="button"
                  variant="text"
                  size="small"
                  label={t('purpose.dialog.previewErrorAction')}
                  onClick={() => void runPreview(selected)}
                />
              </div>
            )}
            {preview.status === 'ready' && (
              <>
                <p className="purpose-parent-dialog__summary">{summaryLine(preview.preview)}</p>
                {renderSummary(preview.preview)}
                <p className="purpose-parent-dialog__body-text">
                  {t('purpose.dialog.confirmBody', {
                    name: selected.name,
                    sphere: sphereName,
                    summary: summaryLine(preview.preview),
                  })}
                </p>
              </>
            )}
            <div className="purpose-parent-dialog__actions">
              <ButtonAtom type="button" variant="text" label={t('purpose.dialog.cancel')} onClick={cancelSelection} disabled={saving} />
              <ButtonAtom
                type="button"
                label={saving ? t('purpose.dialog.saving') : t('purpose.dialog.confirm')}
                onClick={() => void confirm()}
                disabled={preview.status !== 'ready' || saving}
              />
            </div>
          </section>
        )}
      </div>
    </DialogWrapper>
  );
};

export default PurposeParentDialogMolecule;
